import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { BookOpen, CheckCircle, Gift, Mic, Book, Plus } from 'lucide-react';

import { useStories } from '../hooks/useStories';
import { useEchoReaderStories } from '../hooks/useEchoReaderStories';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import StoryViewer from '../components/StoryViewer';
import EchoReader from '../components/EchoReader';
import { getDifficultyColor, getLocalizedDifficultyLabel } from '../utils/difficultyUtils';
import type { Story, EchoReaderStory } from '../types';

const StatCard = ({ icon, label, value, color }: { icon: any, label: string, value: string | number, color: string }) => (
    <div className={`bg-white rounded-2xl p-6 flex items-center shadow-lg transform hover:scale-105 transition-transform duration-300`}>
        <div className={`mr-4 rounded-full p-3 ${color}`}>
            {icon}
        </div>
        <div>
            <div className="text-3xl font-bold text-gray-800">{value}</div>
            <div className="text-sm text-gray-500">{label}</div>
        </div>
    </div>
);

type StoryMode = 'comic' | 'echo_reader';

export default function StoryPage() {
    const { t } = useTranslation();
    const { earnedStories, unviewedCount, isLoading, error, selectStory } = useStories();
    const { 
        stories: echoReaderStories, 
        isLoading: isLoadingEchoReader, 
        isGenerating, 
        generateStory,
        error: echoReaderError 
    } = useEchoReaderStories();
    
    const [selectedStory, setSelectedStory] = useState<Story | null>(null);
    const [selectedEchoReaderStory, setSelectedEchoReaderStory] = useState<EchoReaderStory | null>(null);
    const [currentMode, setCurrentMode] = useState<StoryMode>('comic');

    const handleSelectStory = (story: any) => {
        const fullStory = selectStory(story);
        setSelectedStory(fullStory);
        setSelectedEchoReaderStory(null);
    };

    const handleSelectEchoReaderStory = (story: EchoReaderStory) => {
        setSelectedEchoReaderStory(story);
        setSelectedStory(null);
    };

    const handleGenerateEchoReaderStory = async () => {
        const newStory = await generateStory({
            difficulty_level: 'easy',
            age_group: '7-9',
            story_type: 'echo_reader',
            sentence_count: 5,
            theme: 'adventure',
        });
        
        if (newStory) {
            setSelectedEchoReaderStory(newStory);
            setSelectedStory(null);
        }
    };

    if (isLoading || isLoadingEchoReader) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-yellow-50">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (error || echoReaderError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-red-50 text-red-700">
                Error: {error?.message || echoReaderError}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-blue-50 font-sans">
            <header className="bg-white shadow-md sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <h1 className="text-3xl font-bold text-blue-600 tracking-tight">
                        {t('stories.title', 'My Story Library')}
                    </h1>
                    {(selectedStory || selectedEchoReaderStory) && (
                        <Button 
                            onClick={() => {
                                setSelectedStory(null);
                                setSelectedEchoReaderStory(null);
                            }} 
                            variant="secondary"
                        >
                            {t('common.back', 'Back to Library')}
                        </Button>
                    )}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {selectedStory ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <StoryViewer
                            story={selectedStory}
                            onComplete={() => setSelectedStory(null)}
                            className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8"
                        />
                    </motion.div>
                ) : selectedEchoReaderStory ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <EchoReader
                            story={selectedEchoReaderStory as any}
                            onComplete={() => setSelectedEchoReaderStory(null)}
                            onBack={() => setSelectedEchoReaderStory(null)}
                            className="max-w-4xl mx-auto"
                        />
                    </motion.div>
                ) : (
                    <div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            <StatCard icon={<BookOpen size={28} className="text-white" />} label={t('stories.totalEarned', 'Stories Earned')} value={earnedStories.length} color="bg-blue-500" />
                            <StatCard icon={<CheckCircle size={28} className="text-white" />} label={t('stories.viewed', 'Stories Read')} value={earnedStories.filter(s => s.is_viewed).length} color="bg-green-500" />
                            <StatCard icon={<Gift size={28} className="text-white" />} label={t('stories.newStories', 'New Stories')} value={unviewedCount} color="bg-yellow-500" />
                        </div>

                        {/* Mode Selection */}
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-2xl font-semibold text-gray-800">
                                {t('stories.yourAdventures', 'Your Adventures')}
                            </h2>
                            
                            <div className="flex items-center space-x-2 bg-white rounded-lg p-1 shadow-sm">
                                <Button
                                    variant={currentMode === 'comic' ? 'default' : 'ghost'}
                                    size="sm"
                                    onClick={() => setCurrentMode('comic')}
                                    className="flex items-center space-x-2"
                                >
                                    <Book size={16} />
                                    <span>{t('stories.comicMode', 'Comic Stories')}</span>
                                </Button>
                                <Button
                                    variant={currentMode === 'echo_reader' ? 'default' : 'ghost'}
                                    size="sm"
                                    onClick={() => setCurrentMode('echo_reader')}
                                    className="flex items-center space-x-2"
                                >
                                    <Mic size={16} />
                                    <span>{t('stories.echoReaderMode', 'Echo Reader')}</span>
                                </Button>
                            </div>
                        </div>

                        {/* Content based on selected mode */}
                        {currentMode === 'comic' ? (
                            earnedStories.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {earnedStories.map((story, index) => (
                                    <motion.div
                                        key={story.story_id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className={`bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform hover:-translate-y-2 transition-transform duration-300 ${
                                            !story.is_viewed ? 'border-4 border-yellow-400' : 'border-4 border-transparent'
                                        }`}
                                        onClick={() => handleSelectStory(story)}
                                    >
                                        <div className="p-6">
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="text-xl font-bold text-gray-800">{story.story_title}</h3>
                                                {!story.is_viewed && (
                                                    <div className="bg-yellow-400 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full">NEW</div>
                                                )}
                                            </div>
                                            <p className="text-gray-600 text-sm mb-4 line-clamp-2">{story.story_content}</p>
                                            <div className="flex justify-between items-center text-sm">
                                                <span
                                                    className="font-semibold px-3 py-1 rounded-full text-white"
                                                    style={{ backgroundColor: getDifficultyColor(story.difficulty_level) }}
                                                >
                                                    {getLocalizedDifficultyLabel(story.difficulty_level, t)}
                                                </span>
                                                <span className="text-gray-500">
                                                    {new Date(story.earned_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                            ) : (
                                <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
                                    <img src="/images/empty-box.svg" alt="Empty" className="mx-auto h-32 mb-6" />
                                    <h3 className="text-xl font-semibold text-gray-800">No comic stories yet!</h3>
                                    <p className="text-gray-500 mt-2">Play some games to earn new story rewards.</p>
                                    <Button className="mt-6" onClick={() => window.location.href = '/game'}>
                                        Play Now
                                    </Button>
                                </div>
                            )
                        ) : (
                            /* Echo Reader Mode */
                            <div className="space-y-6">
                                {/* Generate New Story Button */}
                                <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl p-6 text-white">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold mb-2">
                                                {t('echoReader.createNewStory', 'Create a New Pronunciation Story')}
                                            </h3>
                                            <p className="text-purple-100">
                                                {t('echoReader.practiceDescription', 'Practice your pronunciation with AI-generated stories designed for speech learning.')}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={handleGenerateEchoReaderStory}
                                            disabled={isGenerating}
                                            className="bg-white text-purple-600 hover:bg-purple-50 flex items-center space-x-2 ml-4"
                                        >
                                            {isGenerating ? (
                                                <LoadingSpinner size="sm" />
                                            ) : (
                                                <Plus size={16} />
                                            )}
                                            <span>
                                                {isGenerating 
                                                    ? t('echoReader.generating', 'Generating...') 
                                                    : t('echoReader.generate', 'Generate Story')
                                                }
                                            </span>
                                        </Button>
                                    </div>
                                </div>

                                {/* Echo Reader Stories Grid */}
                                {echoReaderStories.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {echoReaderStories.map((story, index) => (
                                            <motion.div
                                                key={story.id}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transform hover:-translate-y-2 transition-transform duration-300 border-2 border-purple-200 hover:border-purple-400"
                                                onClick={() => handleSelectEchoReaderStory(story)}
                                            >
                                                <div className="p-6">
                                                    <div className="flex items-center space-x-2 mb-3">
                                                        <Mic className="text-purple-500" size={20} />
                                                        <span className="text-purple-600 text-sm font-medium">
                                                            {t('echoReader.pronunciationPractice', 'Pronunciation Practice')}
                                                        </span>
                                                    </div>
                                                    
                                                    <h3 className="text-xl font-bold text-gray-800 mb-2">{story.title}</h3>
                                                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{story.content}</p>
                                                    
                                                    <div className="flex justify-between items-center text-sm">
                                                        <div className="flex items-center space-x-2">
                                                            <span
                                                                className="font-semibold px-3 py-1 rounded-full text-white"
                                                                style={{ backgroundColor: getDifficultyColor(story.difficulty === 'easy' ? 1 : story.difficulty === 'medium' ? 3 : 5) }}
                                                            >
                                                                {story.difficulty}
                                                            </span>
                                                            <span className="text-gray-500">
                                                                {story.sentence_count} {t('echoReader.sentences', 'sentences')}
                                                            </span>
                                                        </div>
                                                        <span className="text-gray-500">
                                                            {new Date(story.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-purple-300">
                                        <Mic className="mx-auto h-16 w-16 text-purple-300 mb-4" />
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                            {t('echoReader.noStoriesYet', 'No pronunciation stories yet!')}
                                        </h3>
                                        <p className="text-gray-500 mb-6">
                                            {t('echoReader.getStarted', 'Generate your first story to start practicing pronunciation.')}
                                        </p>
                                        <Button 
                                            onClick={handleGenerateEchoReaderStory}
                                            disabled={isGenerating}
                                            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2 mx-auto"
                                        >
                                            {isGenerating ? (
                                                <LoadingSpinner size="sm" />
                                            ) : (
                                                <Plus size={16} />
                                            )}
                                            <span>
                                                {isGenerating 
                                                    ? t('echoReader.generating', 'Generating...') 
                                                    : t('echoReader.generateFirst', 'Generate Your First Story')
                                                }
                                            </span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}